import React from "react";
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { useLanguage } from "../../context/LanguageContext";

const LeftSidebar = forwardRef(({ onFiltersChange = () => {} }, ref) => {
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";

  const [bodyTypes, setBodyTypes] = useState([]);
  const [selectedBodyTypes, setSelectedBodyTypes] = useState([]);

  const handleBodyTypeChange = (id) => {
    const newSelected = selectedBodyTypes.includes(id)
      ? selectedBodyTypes.filter((typeId) => typeId !== id)
      : [...selectedBodyTypes, id];
    setSelectedBodyTypes(newSelected);

    // Notify parent component of filter changes
    updateParentFilters(newSelected, "bodyTypes");
  };

  const [drivelineTypes, setDrivelineTypes] = useState([]);
  const [selectedDrivelineTypes, setSelectedDrivelineTypes] = useState([]);

  const [transmissionTypes, setTransmissionTypes] = useState([]);
  const [selectedTransmissionTypes, setSelectedTransmissionTypes] = useState(
    []
  );

  const [fuelTypes, setFuelTypes] = useState([]);
  const [selectedFuelTypes, setSelectedFuelTypes] = useState([]);

  const [featuredFilter, setFeaturedFilter] = useState(""); // "yes", "no", or ""

  // New filter states
  const [cylinders, setCylinders] = useState([]);
  const [selectedCylinders, setSelectedCylinders] = useState([]);

  const [engines, setEngines] = useState([]);
  const [selectedEngines, setSelectedEngines] = useState([]);

  const [highlights, setHighlights] = useState([]);
  const [selectedHighlights, setSelectedHighlights] = useState([]);

  const [specifications, setSpecifications] = useState([]);
  const [selectedSpecifications, setSelectedSpecifications] = useState([]);

  const [exteriorColors, setExteriorColors] = useState([]);
  const [selectedExteriorColors, setSelectedExteriorColors] = useState([]);

  const [interiorColors, setInteriorColors] = useState([]);
  const [selectedInteriorColors, setSelectedInteriorColors] = useState([]);

  const [primaryDamageTypes, setPrimaryDamageTypes] = useState([]);
  const [selectedPrimaryDamageTypes, setSelectedPrimaryDamageTypes] = useState(
    []
  );

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/bodytypes`)
      .then((res) => res.json())
      .then((data) => setBodyTypes(data))
      .catch((err) => console.error(err));

    fetch(`${process.env.REACT_APP_API_URL}/drivelinetypes`)
      .then((res) => res.json())
      .then((data) => setDrivelineTypes(data))
      .catch((err) => console.error(err));

    fetch(`${process.env.REACT_APP_API_URL}/transmissiontypes`)
      .then((res) => res.json())
      .then((data) => setTransmissionTypes(data))
      .catch((err) => console.error(err));

    fetch(`${process.env.REACT_APP_API_URL}/fueltypes`)
      .then((res) => res.json())
      .then((data) => setFuelTypes(data))
      .catch((err) => console.error(err));

    // Fetch new filter data
    fetch(`${process.env.REACT_APP_API_URL}/cylinders`)
      .then((res) => res.json())
      .then((data) => setCylinders(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Error fetching cylinders:", err);
        setCylinders([]);
      });

    fetch(`${process.env.REACT_APP_API_URL}/engines`)
      .then((res) => res.json())
      .then((data) => setEngines(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Error fetching engines:", err);
        setEngines([]);
      });

    fetch(`${process.env.REACT_APP_API_URL}/highlights`)
      .then((res) => res.json())
      .then((data) => setHighlights(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Error fetching highlights:", err);
        setHighlights([]);
      });

    fetch(`${process.env.REACT_APP_API_URL}/specifications`)
      .then((res) => res.json())
      .then((data) => setSpecifications(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Error fetching specifications:", err);
        setSpecifications([]);
      });

    fetch(`${process.env.REACT_APP_API_URL}/exteriorcolors`)
      .then((res) => res.json())
      .then((data) => setExteriorColors(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Error fetching exterior colors:", err);
        setExteriorColors([]);
      });

    fetch(`${process.env.REACT_APP_API_URL}/interiorcolors`)
      .then((res) => res.json())
      .then((data) => setInteriorColors(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Error fetching interior colors:", err);
        setInteriorColors([]);
      });

    fetch(`${process.env.REACT_APP_API_URL}/damagetypes`)
      .then((res) => res.json())
      .then((data) => setPrimaryDamageTypes(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error("Error fetching primary damage types:", err);
        setPrimaryDamageTypes([]);
      });
  }, []);

  const handleDrivelineTypeChange = (id) => {
    const newSelected = selectedDrivelineTypes.includes(id)
      ? selectedDrivelineTypes.filter((typeId) => typeId !== id)
      : [...selectedDrivelineTypes, id];
    setSelectedDrivelineTypes(newSelected);

    // Notify parent component of filter changes
    updateParentFilters(newSelected, "drivelineTypes");
  };

  const handleTransmissionTypeChange = (id) => {
    const newSelected = selectedTransmissionTypes.includes(id)
      ? selectedTransmissionTypes.filter((typeId) => typeId !== id)
      : [...selectedTransmissionTypes, id];
    setSelectedTransmissionTypes(newSelected);

    // Notify parent component of filter changes
    updateParentFilters(newSelected, "transmissionTypes");
  };

  const handleFuelTypeChange = (id) => {
    const newSelected = selectedFuelTypes.includes(id)
      ? selectedFuelTypes.filter((typeId) => typeId !== id)
      : [...selectedFuelTypes, id];
    setSelectedFuelTypes(newSelected);

    // Notify parent component of filter changes
    updateParentFilters(newSelected, "fuelTypes");
  };

  const handleFeaturedChange = (value) => {
    setFeaturedFilter(value);
    // Notify parent component of filter changes
    updateParentFilters(value, "featured");
  };

  // New filter handlers
  const handleCylinderChange = (id) => {
    const newSelected = selectedCylinders.includes(id)
      ? selectedCylinders.filter((typeId) => typeId !== id)
      : [...selectedCylinders, id];
    setSelectedCylinders(newSelected);
    updateParentFilters(newSelected, "cylinders");
  };

  const handleEngineChange = (id) => {
    const newSelected = selectedEngines.includes(id)
      ? selectedEngines.filter((typeId) => typeId !== id)
      : [...selectedEngines, id];
    setSelectedEngines(newSelected);
    updateParentFilters(newSelected, "engines");
  };

  const handleHighlightChange = (id) => {
    const newSelected = selectedHighlights.includes(id)
      ? selectedHighlights.filter((typeId) => typeId !== id)
      : [...selectedHighlights, id];
    setSelectedHighlights(newSelected);
    updateParentFilters(newSelected, "highlights");
  };

  const handleSpecificationChange = (id) => {
    const newSelected = selectedSpecifications.includes(id)
      ? selectedSpecifications.filter((typeId) => typeId !== id)
      : [...selectedSpecifications, id];
    setSelectedSpecifications(newSelected);
    updateParentFilters(newSelected, "specifications");
  };

  const handleExteriorColorChange = (id) => {
    const newSelected = selectedExteriorColors.includes(id)
      ? selectedExteriorColors.filter((typeId) => typeId !== id)
      : [...selectedExteriorColors, id];
    setSelectedExteriorColors(newSelected);
    updateParentFilters(newSelected, "exteriorColors");
  };

  const handleInteriorColorChange = (id) => {
    const newSelected = selectedInteriorColors.includes(id)
      ? selectedInteriorColors.filter((typeId) => typeId !== id)
      : [...selectedInteriorColors, id];
    setSelectedInteriorColors(newSelected);
    updateParentFilters(newSelected, "interiorColors");
  };

  const handlePrimaryDamageTypeChange = (id) => {
    const newSelected = selectedPrimaryDamageTypes.includes(id)
      ? selectedPrimaryDamageTypes.filter((typeId) => typeId !== id)
      : [...selectedPrimaryDamageTypes, id];
    setSelectedPrimaryDamageTypes(newSelected);
    updateParentFilters(newSelected, "primaryDamageTypes");
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    setFeaturedFilter: (value) => {
      const normalized = value === "yes" ? "yes" : value === "no" ? "no" : "";
      setFeaturedFilter(normalized);
      updateParentFilters(normalized, "featured");
    },
    removeFilter: (filterType, filterId) => {
      switch (filterType) {
        case "bodyTypes":
          setSelectedBodyTypes((prev) => {
            const newSelected = prev.filter((id) => id !== filterId);
            updateParentFilters(newSelected, "bodyTypes");
            return newSelected;
          });
          break;
        case "drivelineTypes":
          setSelectedDrivelineTypes((prev) => {
            const newSelected = prev.filter((id) => id !== filterId);
            updateParentFilters(newSelected, "drivelineTypes");
            return newSelected;
          });
          break;
        case "transmissionTypes":
          setSelectedTransmissionTypes((prev) => {
            const newSelected = prev.filter((id) => id !== filterId);
            updateParentFilters(newSelected, "transmissionTypes");
            return newSelected;
          });
          break;
        case "fuelTypes":
          setSelectedFuelTypes((prev) => {
            const newSelected = prev.filter((id) => id !== filterId);
            updateParentFilters(newSelected, "fuelTypes");
            return newSelected;
          });
          break;
        case "featured":
          setFeaturedFilter("");
          updateParentFilters("", "featured");
          break;
        case "cylinders":
          setSelectedCylinders((prev) => {
            const newSelected = prev.filter((id) => id !== filterId);
            updateParentFilters(newSelected, "cylinders");
            return newSelected;
          });
          break;
        case "engines":
          setSelectedEngines((prev) => {
            const newSelected = prev.filter((id) => id !== filterId);
            updateParentFilters(newSelected, "engines");
            return newSelected;
          });
          break;
        case "highlights":
          setSelectedHighlights((prev) => {
            const newSelected = prev.filter((id) => id !== filterId);
            updateParentFilters(newSelected, "highlights");
            return newSelected;
          });
          break;
        case "specifications":
          setSelectedSpecifications((prev) => {
            const newSelected = prev.filter((id) => id !== filterId);
            updateParentFilters(newSelected, "specifications");
            return newSelected;
          });
          break;
        case "exteriorColors":
          setSelectedExteriorColors((prev) => {
            const newSelected = prev.filter((id) => id !== filterId);
            updateParentFilters(newSelected, "exteriorColors");
            return newSelected;
          });
          break;
        case "interiorColors":
          setSelectedInteriorColors((prev) => {
            const newSelected = prev.filter((id) => id !== filterId);
            updateParentFilters(newSelected, "interiorColors");
            return newSelected;
          });
          break;
        case "primaryDamageTypes":
          setSelectedPrimaryDamageTypes((prev) => {
            const newSelected = prev.filter((id) => id !== filterId);
            updateParentFilters(newSelected, "primaryDamageTypes");
            return newSelected;
          });
          break;
        default:
          break;
      }
    },
    clearFilterType: (filterType) => {
      switch (filterType) {
        case "bodyTypes":
          setSelectedBodyTypes([]);
          updateParentFilters([], "bodyTypes");
          break;
        case "drivelineTypes":
          setSelectedDrivelineTypes([]);
          updateParentFilters([], "drivelineTypes");
          break;
        case "transmissionTypes":
          setSelectedTransmissionTypes([]);
          updateParentFilters([], "transmissionTypes");
          break;
        case "fuelTypes":
          setSelectedFuelTypes([]);
          updateParentFilters([], "fuelTypes");
          break;
        case "featured":
          setFeaturedFilter("");
          updateParentFilters("", "featured");
          break;
        case "cylinders":
          setSelectedCylinders([]);
          updateParentFilters([], "cylinders");
          break;
        case "engines":
          setSelectedEngines([]);
          updateParentFilters([], "engines");
          break;
        case "highlights":
          setSelectedHighlights([]);
          updateParentFilters([], "highlights");
          break;
        case "specifications":
          setSelectedSpecifications([]);
          updateParentFilters([], "specifications");
          break;
        case "exteriorColors":
          setSelectedExteriorColors([]);
          updateParentFilters([], "exteriorColors");
          break;
        case "interiorColors":
          setSelectedInteriorColors([]);
          updateParentFilters([], "interiorColors");
          break;
        case "primaryDamageTypes":
          setSelectedPrimaryDamageTypes([]);
          updateParentFilters([], "primaryDamageTypes");
          break;
        default:
          break;
      }
    },
    resetAllFilters: () => {
      // Reset all filter states to initial values
      setSelectedBodyTypes([]);
      setSelectedDrivelineTypes([]);
      setSelectedTransmissionTypes([]);
      setSelectedFuelTypes([]);
      setFeaturedFilter("");
      setSelectedCylinders([]);
      setSelectedEngines([]);
      setSelectedHighlights([]);
      setSelectedSpecifications([]);
      setSelectedExteriorColors([]);
      setSelectedInteriorColors([]);
      setSelectedPrimaryDamageTypes([]);

      // Notify parent component with empty filters
      const emptyFilters = {
        bodyTypes: [],
        drivelineTypes: [],
        transmissionTypes: [],
        fuelTypes: [],
        featured: "",
        cylinders: [],
        engines: [],
        highlights: [],
        specifications: [],
        exteriorColors: [],
        interiorColors: [],
        primaryDamageTypes: [],
      };

      onFiltersChange(emptyFilters, {});
    },
  }));

  // Helper function to update parent with current filter state
  const updateParentFilters = (newSelected, filterType) => {
    const currentFilters = {
      bodyTypes: selectedBodyTypes,
      drivelineTypes: selectedDrivelineTypes,
      transmissionTypes: selectedTransmissionTypes,
      fuelTypes: selectedFuelTypes,
      featured: featuredFilter,
      cylinders: selectedCylinders,
      engines: selectedEngines,
      highlights: selectedHighlights,
      specifications: selectedSpecifications,
      exteriorColors: selectedExteriorColors,
      interiorColors: selectedInteriorColors,
      primaryDamageTypes: selectedPrimaryDamageTypes,
      [filterType]: newSelected,
    };

    // Get the actual names for the selected IDs
    const filterData = {
      bodyTypes: bodyTypes,
      drivelineTypes: drivelineTypes,
      transmissionTypes: transmissionTypes,
      fuelTypes: fuelTypes,
      featured: [
        { id: "yes", name: "Yes" },
        { id: "no", name: "No" },
      ],
      cylinders: cylinders,
      engines: engines,
      highlights: highlights,
      specifications: specifications,
      exteriorColors: exteriorColors,
      interiorColors: interiorColors,
      primaryDamageTypes: primaryDamageTypes,
    };

    onFiltersChange(currentFilters, filterData);
  };

  /* Expand/collapse logic for all filter types */
  const [featuredExpanded, setFeaturedExpanded] = useState(false);
  const [bodyTypesExpanded, setBodyTypesExpanded] = useState(false);
  const [drivelineTypesExpanded, setDrivelineTypesExpanded] = useState(false);
  const [transmissionTypesExpanded, setTransmissionTypesExpanded] =
    useState(false);
  const [fuelTypesExpanded, setFuelTypesExpanded] = useState(false);
  const [cylindersExpanded, setCylindersExpanded] = useState(false);
  const [enginesExpanded, setEnginesExpanded] = useState(false);
  const [highlightsExpanded, setHighlightsExpanded] = useState(false);
  const [specificationsExpanded, setSpecificationsExpanded] = useState(false);
  const [exteriorColorsExpanded, setExteriorColorsExpanded] = useState(false);
  const [interiorColorsExpanded, setInteriorColorsExpanded] = useState(false);
  const [primaryDamageTypesExpanded, setPrimaryDamageTypesExpanded] =
    useState(false);

  const toggleFeatured = () => setFeaturedExpanded((prev) => !prev);
  const toggleBodyTypes = () => setBodyTypesExpanded((prev) => !prev);
  const toggleDrivelineTypes = () => setDrivelineTypesExpanded((prev) => !prev);
  const toggleTransmissionTypes = () =>
    setTransmissionTypesExpanded((prev) => !prev);
  const toggleFuelTypes = () => setFuelTypesExpanded((prev) => !prev);
  const toggleCylinders = () => setCylindersExpanded((prev) => !prev);
  const toggleEngines = () => setEnginesExpanded((prev) => !prev);
  const toggleHighlights = () => setHighlightsExpanded((prev) => !prev);
  const toggleSpecifications = () => setSpecificationsExpanded((prev) => !prev);
  const toggleExteriorColors = () => setExteriorColorsExpanded((prev) => !prev);
  const toggleInteriorColors = () => setInteriorColorsExpanded((prev) => !prev);
  const togglePrimaryDamageTypes = () =>
    setPrimaryDamageTypesExpanded((prev) => !prev);

  /* Update the return JSX to use expandable sections */
  return (
    <aside className="p-4" dir={isRTL ? "rtl" : "ltr"}>
      {/* Featured Filter */}
      <div className="mb-4">
        <button
          type="button"
          onClick={toggleFeatured}
          className="flex items-center w-full text-white font-semibold mb-2 focus:outline-none transition-colors hover:text-gray-500"
          aria-expanded={featuredExpanded}
        >
          <span>{translate("sidebar.filters.featured")}</span>
          <span className={isRTL ? "mr-auto" : "ml-auto"}>
            {featuredExpanded ? "−" : "+"}
          </span>
        </button>
        {featuredExpanded && (
          <div
            className={`${isRTL ? "pr-4 border-r-2" : "pl-4 border-l-2"} mb-2`}
          >
            <label className="flex text-white text-sm items-center cursor-pointer mb-2">
              <input
                type="radio"
                name="featured"
                value="yes"
                checked={featuredFilter === "yes"}
                onChange={() => handleFeaturedChange("yes")}
                className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
              />
              <span className="px-1 py-1">
                {translate("sidebar.options.yes")}
              </span>
            </label>
            <label className="flex text-white text-sm items-center cursor-pointer mb-2">
              <input
                type="radio"
                name="featured"
                value="no"
                checked={featuredFilter === "no"}
                onChange={() => handleFeaturedChange("no")}
                className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
              />
              <span className="px-1 py-1">
                {translate("sidebar.options.no")}
              </span>
            </label>
            <label className="flex text-white text-sm items-center cursor-pointer">
              <input
                type="radio"
                name="featured"
                value=""
                checked={featuredFilter === ""}
                onChange={() => handleFeaturedChange("")}
                className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
              />
              <span className="px-1 py-1">
                {translate("sidebar.options.all")}
              </span>
            </label>
          </div>
        )}
      </div>
      <div>
        <button
          type="button"
          onClick={toggleBodyTypes}
          className="flex items-center w-full text-white font-semibold mb-2 focus:outline-none transition-colors hover:text-gray-500"
          aria-expanded={bodyTypesExpanded}
        >
          <span>{translate("sidebar.filters.bodyType")}</span>
          <span className={isRTL ? "mr-auto" : "ml-auto"}>
            {bodyTypesExpanded ? "−" : "+"}
          </span>
        </button>
        {bodyTypesExpanded && (
          <ul
            className={`${isRTL ? "pr-4 border-r-2" : "pl-4 border-l-2"} mb-2`}
          >
            {bodyTypes.map((type) => (
              <li key={type.id} className="mb-2">
                <label className="flex text-white text-sm items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedBodyTypes.includes(type.id)}
                    onChange={() => handleBodyTypeChange(type.id)}
                    className={`${isRTL ? "ml-2" : "mr-2"}`}
                  />
                  <span className="px-1 py-1">{type.name}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* Driveline Filter */}
      <div className="mt-4">
        <button
          type="button"
          onClick={toggleDrivelineTypes}
          className="flex items-center w-full text-white font-semibold  mb-2 focus:outline-none transition-colors hover:text-gray-500"
          aria-expanded={drivelineTypesExpanded}
        >
          <span>{translate("sidebar.filters.driveline")}</span>
          <span className={isRTL ? "mr-auto" : "ml-auto"}>
            {drivelineTypesExpanded ? "−" : "+"}
          </span>
        </button>
        {drivelineTypesExpanded && (
          <ul
            className={`${isRTL ? "pr-4 border-r-2" : "pl-4 border-l-2"}  mb-2`}
          >
            {drivelineTypes.map((type) => (
              <li key={type.id} className="mb-2">
                <label className="flex text-white text-sm items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedDrivelineTypes.includes(type.id)}
                    onChange={() => handleDrivelineTypeChange(type.id)}
                    className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
                  />
                  <span className="px-1 py-1">{type.name}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* Transmission Filter */}
      <div className="mt-4">
        <button
          type="button"
          onClick={toggleTransmissionTypes}
          className="flex items-center w-full text-white font-semibold mb-2 focus:outline-none transition-colors hover:text-gray-500"
          aria-expanded={transmissionTypesExpanded}
        >
          <span>{translate("sidebar.filters.transmission")}</span>
          <span className={isRTL ? "mr-auto" : "ml-auto"}>
            {transmissionTypesExpanded ? "−" : "+"}
          </span>
        </button>
        {transmissionTypesExpanded && (
          <ul
            className={`${isRTL ? "pr-4 border-r-2" : "pl-4 border-l-2"} mb-2`}
          >
            {transmissionTypes.map((type) => (
              <li key={type.id} className="mb-2">
                <label className="flex text-white text-sm items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedTransmissionTypes.includes(type.id)}
                    onChange={() => handleTransmissionTypeChange(type.id)}
                    className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
                  />
                  <span className="px-1 py-1">{type.name}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* Fuel Filter */}
      <div className="mt-4">
        <button
          type="button"
          onClick={toggleFuelTypes}
          className="flex items-center w-full text-white font-semibold mb-2 focus:outline-none transition-colors hover:text-gray-500"
          aria-expanded={fuelTypesExpanded}
        >
          <span>{translate("sidebar.filters.fuel")}</span>
          <span className={isRTL ? "mr-auto" : "ml-auto"}>
            {fuelTypesExpanded ? "−" : "+"}
          </span>
        </button>
        {fuelTypesExpanded && (
          <ul
            className={`${isRTL ? "pr-4 border-r-2" : "pl-4 border-l-2"} mb-2`}
          >
            {fuelTypes.map((type) => (
              <li key={type.id} className="mb-2">
                <label className="flex text-white text-sm items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedFuelTypes.includes(type.id)}
                    onChange={() => handleFuelTypeChange(type.id)}
                    className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
                  />
                  <span className="px-1 py-1">{type.name}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* Cylinders Filter */}
      <div className="mt-4">
        <button
          type="button"
          onClick={toggleCylinders}
          className="flex items-center w-full text-white font-semibold mb-2 focus:outline-none transition-colors hover:text-gray-500"
          aria-expanded={cylindersExpanded}
        >
          <span>{translate("sidebar.filters.cylinders")}</span>
          <span className={isRTL ? "mr-auto" : "ml-auto"}>
            {cylindersExpanded ? "−" : "+"}
          </span>
        </button>
        {cylindersExpanded && (
          <ul
            className={`${isRTL ? "pr-4 border-r-2" : "pl-4 border-l-2"} mb-2`}
          >
            {Array.isArray(cylinders) &&
              cylinders.map((cylinder) => (
                <li key={cylinder.id} className="mb-2">
                  <label className="flex text-white text-sm items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCylinders.includes(cylinder.id)}
                      onChange={() => handleCylinderChange(cylinder.id)}
                      className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
                    />
                    <span className="px-1 py-1">{cylinder.name}</span>
                  </label>
                </li>
              ))}
          </ul>
        )}
      </div>
      {/* Engines Filter */}
      <div className="mt-4">
        <button
          type="button"
          onClick={toggleEngines}
          className="flex items-center w-full text-white font-semibold mb-2 focus:outline-none transition-colors hover:text-gray-500"
          aria-expanded={enginesExpanded}
        >
          <span>{translate("sidebar.filters.engines")}</span>
          <span className={isRTL ? "mr-auto" : "ml-auto"}>
            {enginesExpanded ? "−" : "+"}
          </span>
        </button>
        {enginesExpanded && (
          <ul
            className={`${isRTL ? "pr-4 border-r-2" : "pl-4 border-l-2"} mb-2`}
          >
            {Array.isArray(engines) &&
              engines.map((engine) => (
                <li key={engine.id} className="mb-2">
                  <label className="flex text-white text-sm items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedEngines.includes(engine.id)}
                      onChange={() => handleEngineChange(engine.id)}
                      className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
                    />
                    <span className="px-1 py-1">{engine.name}</span>
                  </label>
                </li>
              ))}
          </ul>
        )}
      </div>
      {/* Highlights Filter */}
      <div className="mt-4">
        <button
          type="button"
          onClick={toggleHighlights}
          className="flex items-center w-full text-white font-semibold mb-2 focus:outline-none transition-colors hover:text-gray-500"
          aria-expanded={highlightsExpanded}
        >
          <span>{translate("sidebar.filters.highlights")}</span>
          <span className={isRTL ? "mr-auto" : "ml-auto"}>
            {highlightsExpanded ? "−" : "+"}
          </span>
        </button>
        {highlightsExpanded && (
          <ul
            className={`${isRTL ? "pr-4 border-r-2" : "pl-4 border-l-2"} mb-2`}
          >
            {Array.isArray(highlights) &&
              highlights.map((highlight) => (
                <li key={highlight.id} className="mb-2">
                  <label className="flex text-white text-sm items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedHighlights.includes(highlight.id)}
                      onChange={() => handleHighlightChange(highlight.id)}
                      className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
                    />
                    <span className="px-1 py-1">{highlight.name}</span>
                  </label>
                </li>
              ))}
          </ul>
        )}
      </div>
      {/* Exterior Colors Filter */}
      <div className="mt-4">
        <button
          type="button"
          onClick={toggleExteriorColors}
          className="flex items-center w-full text-white font-semibold mb-2 focus:outline-none transition-colors hover:text-gray-500"
          aria-expanded={exteriorColorsExpanded}
        >
          <span>{translate("sidebar.filters.exteriorColors")}</span>
          <span className={isRTL ? "mr-auto" : "ml-auto"}>
            {exteriorColorsExpanded ? "−" : "+"}
          </span>
        </button>
        {exteriorColorsExpanded && (
          <ul
            className={`${isRTL ? "pr-4 border-r-2" : "pl-4 border-l-2"} mb-2`}
          >
            {Array.isArray(exteriorColors) &&
              exteriorColors.map((color) => (
                <li key={color.id} className="mb-2">
                  <label className="flex text-white text-sm items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedExteriorColors.includes(color.id)}
                      onChange={() => handleExteriorColorChange(color.id)}
                      className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
                    />
                    <span className="px-1 py-1">{color.name}</span>
                  </label>
                </li>
              ))}
          </ul>
        )}
      </div>
      {/* Interior Colors Filter */}
      <div className="mt-4">
        <button
          type="button"
          onClick={toggleInteriorColors}
          className="flex items-center w-full text-white font-semibold mb-2 focus:outline-none transition-colors hover:text-gray-500"
          aria-expanded={interiorColorsExpanded}
        >
          <span>{translate("sidebar.filters.interiorColors")}</span>
          <span className={isRTL ? "mr-auto" : "ml-auto"}>
            {interiorColorsExpanded ? "−" : "+"}
          </span>
        </button>
        {interiorColorsExpanded && (
          <ul
            className={`${isRTL ? "pr-4 border-r-2" : "pl-4 border-l-2"} mb-2`}
          >
            {Array.isArray(interiorColors) &&
              interiorColors.map((color) => (
                <li key={color.id} className="mb-2">
                  <label className="flex text-white text-sm items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedInteriorColors.includes(color.id)}
                      onChange={() => handleInteriorColorChange(color.id)}
                      className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
                    />
                    <span className="px-1 py-1">{color.name}</span>
                  </label>
                </li>
              ))}
          </ul>
        )}
      </div>
      {/* Primary Damage Types Filter */}
      <div className="mt-4">
        <button
          type="button"
          onClick={togglePrimaryDamageTypes}
          className="flex items-center w-full text-white font-semibold mb-2 focus:outline-none transition-colors hover:text-gray-500"
          aria-expanded={primaryDamageTypesExpanded}
        >
          <span>{translate("sidebar.filters.damageTypes")}</span>
          <span className={isRTL ? "mr-auto" : "ml-auto"}>
            {primaryDamageTypesExpanded ? "−" : "+"}
          </span>
        </button>
        {primaryDamageTypesExpanded && (
          <ul
            className={`${isRTL ? "pr-4 border-r-2" : "pl-4 border-l-2"} mb-2`}
          >
            {Array.isArray(primaryDamageTypes) &&
              primaryDamageTypes.map((damageType) => (
                <li key={damageType.id} className="mb-2">
                  <label className="flex text-white text-sm items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedPrimaryDamageTypes.includes(
                        damageType.id
                      )}
                      onChange={() =>
                        handlePrimaryDamageTypeChange(damageType.id)
                      }
                      className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
                    />
                    <span className="px-1 py-1">{damageType.name}</span>
                  </label>
                </li>
              ))}
          </ul>
        )}
      </div>
      {/* Specifications Filter */}
      <div className="mt-4">
        <button
          type="button"
          onClick={toggleSpecifications}
          className="flex items-center w-full text-white font-semibold mb-2 focus:outline-none transition-colors hover:text-gray-500"
          aria-expanded={specificationsExpanded}
        >
          <span>{translate("sidebar.filters.specifications")}</span>
          <span className={isRTL ? "mr-auto" : "ml-auto"}>
            {specificationsExpanded ? "−" : "+"}
          </span>
        </button>
        {specificationsExpanded && (
          <ul
            className={`${isRTL ? "pr-4 border-r-2" : "pl-4 border-l-2"} mb-2`}
          >
            {Array.isArray(specifications) &&
              specifications.map((specification) => (
                <li key={specification.id} className="mb-2">
                  <label className="flex text-white text-sm items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedSpecifications.includes(
                        specification.id
                      )}
                      onChange={() =>
                        handleSpecificationChange(specification.id)
                      }
                      className={`${isRTL ? "ml-2" : "mr-2"} accent-blue-600`}
                    />
                    <span className="px-1 py-1">{specification.name}</span>
                  </label>
                </li>
              ))}
          </ul>
        )}
      </div>
    </aside>
  );
});
export default LeftSidebar;
